import reactifyWc from 'reactify-wc';
import { __awaiter, __rest } from 'tslib';
import { BOTTOM_SHEET_CONSTANTS, BUSY_INDICATOR_CONSTANTS, DIALOG_CONSTANTS, POPUP_CONSTANTS, TOAST_CONSTANTS, PopupAnimationType } from '@tylertech/forge';
import { jsx } from 'react/jsx-runtime';
import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';

/**
 * Creates a React component proxy (wrapper) around an HTML element or custom element).
 * @param name The name of the HTML element to create.
 * @param options
 * @returns
 */
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function createElementProxy(name, options) {
    return reactifyWc(name, options);
}

const ForgeAccordion = createElementProxy('forge-accordion');
const ForgeAppBar = createElementProxy('forge-app-bar');
const ForgeAppBarHelpButton = createElementProxy('forge-app-bar-help-button');
const ForgeAppBarMenuButton = createElementProxy('forge-app-bar-menu-button');
const ForgeAppBarNotificationButton = createElementProxy('forge-app-bar-notification-button');
const ForgeAppBarProfileButton = createElementProxy('forge-app-bar-profile-button');
const ForgeAppBarSearch = createElementProxy('forge-app-bar-search');
const ForgeAutocomplete = createElementProxy('forge-autocomplete');
const ForgeAvatar = createElementProxy('forge-avatar');
const ForgeBackdrop = createElementProxy('forge-backdrop');
const ForgeBadge = createElementProxy('forge-badge');
const ForgeBanner = createElementProxy('forge-banner');
const ForgeButtonArea = createElementProxy('forge-button-area');
const ForgeButton = createElementProxy('forge-button');
const ForgeButtonToggleGroup = createElementProxy('forge-button-toggle-group');
const ForgeButtonToggle = createElementProxy('forge-button-toggle');
const ForgeCalendar = createElementProxy('forge-calendar');
const ForgeCard = createElementProxy('forge-card');
const ForgeCheckbox = createElementProxy('forge-checkbox');
const ForgeChipField = createElementProxy('forge-chip-field');
const ForgeChipSet = createElementProxy('forge-chip-set');
const ForgeChip = createElementProxy('forge-chip');
const ForgeCircularProgress = createElementProxy('forge-circular-progress');
const ForgeColorPicker = createElementProxy('forge-color-picker');
const ForgeDateRangePicker = createElementProxy('forge-date-range-picker');
const ForgeDatePicker = createElementProxy('forge-date-picker');
const ForgeDivider = createElementProxy('forge-divider');
const ForgeDrawer = createElementProxy('forge-drawer');
const ForgeMiniDrawer = createElementProxy('forge-mini-drawer');
const ForgeModalDrawer = createElementProxy('forge-modal-drawer');
const ForgeExpansionPanel = createElementProxy('forge-expansion-panel');
const ForgeFilePicker = createElementProxy('forge-file-picker');
const ForgeFloatingActionButton = createElementProxy('forge-fab');
const ForgeIcon = createElementProxy('forge-icon');
const ForgeIconButton = createElementProxy('forge-icon-button');
const ForgeInlineMessage = createElementProxy('forge-inline-message');
const ForgeKeyboardShortcut = createElementProxy('forge-keyboard-shortcut');
const ForgeLabelValue = createElementProxy('forge-label-value');
const ForgeLinearProgress = createElementProxy('forge-linear-progress');
const ForgeList = createElementProxy('forge-list');
const ForgeListItem = createElementProxy('forge-list-item');
const ForgeMenu = createElementProxy('forge-menu');
const ForgeProfileCard = createElementProxy('forge-profile-card');
const ForgeOpenIcon = createElementProxy('forge-open-icon');
const ForgePageState = createElementProxy('forge-page-state');
const ForgePaginator = createElementProxy('forge-paginator');
const ForgeProductIcon = createElementProxy('forge-product-icon');
const ForgeQuantityField = createElementProxy('forge-quantity-field');
const ForgeRadio = createElementProxy('forge-radio');
const ForgeRipple = createElementProxy('forge-ripple');
const ForgeScaffold = createElementProxy('forge-scaffold');
const ForgeSelect = createElementProxy('forge-select');
const ForgeSelectDropdown = createElementProxy('forge-select-dropdown');
const ForgeOption = createElementProxy('forge-option', { forceProperty: ['value'] });
const ForgeOptionGroup = createElementProxy('forge-option-group');
const ForgeSkeleton = createElementProxy('forge-skeleton');
const ForgeSlider = createElementProxy('forge-slider');
const ForgeSplitView = createElementProxy('forge-split-view');
const ForgeSplitViewPanel = createElementProxy('forge-split-view-panel');
const ForgeStack = createElementProxy('forge-stack');
const ForgeStepper = createElementProxy('forge-stepper');
const ForgeStep = createElementProxy('forge-step');
const ForgeSwitch = createElementProxy('forge-switch');
const ForgeTable = createElementProxy('forge-table');
const ForgeTabBar = createElementProxy('forge-tab-bar');
const ForgeTab = createElementProxy('forge-tab');
const ForgeTextField = createElementProxy('forge-text-field');
const ForgeTimePicker = createElementProxy('forge-time-picker');
const ForgeToolbar = createElementProxy('forge-toolbar');
const ForgeTooltip = createElementProxy('forge-tooltip');
const ForgeViewSwitcher = createElementProxy('forge-view-switcher');
const ForgeView = createElementProxy('forge-view');

function setRef(ref, value) {
    if (typeof ref === 'function') {
        ref(value);
    }
    else if (ref != null) {
        ref.current = value;
    }
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const createOverlayComponent = (controller) => {
    class Overlay extends React.Component {
        constructor(props) {
            super(props);
            this.isDismissing = false;
            this.handleDismiss = this.handleDismiss.bind(this);
        }
        componentDidMount() {
            if (this.props.open) {
                this.present();
            }
        }
        componentWillUnmount() {
            if (this.overlay) {
                if (this.overlay.isConnected) {
                    this.overlay.remove();
                }
                this.overlay = undefined;
            }
        }
        handleDismiss() {
            if (typeof this.props.onDismiss === 'function') {
                this.props.onDismiss();
            }
            setRef(this.props.forwardedRef, null);
        }
        shouldComponentUpdate(nextProps) {
            // If the overlay is about to dismiss, make sure we set our dismissing flag
            // to keep rendering the overlay element to allow for animations
            if (this.overlay && nextProps.open !== this.props.open && !nextProps.open) {
                this.isDismissing = true;
            }
            return true;
        }
        componentDidUpdate(prevProps) {
            return __awaiter(this, void 0, void 0, function* () {
                if (prevProps.open !== this.props.open && this.props.open) {
                    this.present();
                }
                // Listen for when options changes so we can update the overlay element
                if (controller.update && this.props.open && this.overlay && prevProps.options !== this.props.options) {
                    controller.update(this.overlay, this.props);
                }
                if (this.overlay && prevProps.open !== this.props.open && !this.props.open) {
                    yield controller.hide(this.overlay);
                    this.isDismissing = false;
                    this.forceUpdate();
                }
            });
        }
        present() {
            return __awaiter(this, void 0, void 0, function* () {
                this.overlay = controller.create(this.props, this.handleDismiss);
                // We need to force a re-render to use our new overlay element for the portal container
                this.forceUpdate();
                setRef(this.props.forwardedRef, this.overlay);
                yield controller.show(this.overlay);
            });
        }
        render() {
            return this.overlay ? ReactDOM.createPortal(this.props.open || this.isDismissing ? this.props.children : null, this.overlay) : null;
        }
    }
    // eslint-disable-next-line arrow-body-style
    return React.forwardRef((props, ref) => {
        return jsx(Overlay, Object.assign({}, props, { forwardedRef: ref }));
    });
};

const BOTTOM_SHEET_CLOSE_ANIMATION_DURATION$1 = 200;
const controller$3 = {
    create: createBottomSheet,
    show: element => element.open = true,
    // eslint-disable-next-line arrow-body-style
    hide: element => {
        return new Promise(resolve => {
            element.open = false;
            setTimeout(() => resolve(), BOTTOM_SHEET_CLOSE_ANIMATION_DURATION$1);
        });
    }
};
function createBottomSheet({ options }, dismissCallback) {
    const element = document.createElement('forge-bottom-sheet');
    if (options) {
        const { bottomSheetAttributes, bottomSheetClass } = options, rest = __rest(options, ["bottomSheetAttributes", "bottomSheetClass"]);
        Object.assign(element, rest);
        if (bottomSheetAttributes) {
            bottomSheetAttributes.forEach((value, key) => element.setAttribute(key, value));
        }
        if (bottomSheetClass) {
            element.classList.add(bottomSheetClass);
        }
    }
    // Listen for the close event in case the bottom sheet is closed via escape key or backdrop click
    element.addEventListener(BOTTOM_SHEET_CONSTANTS.events.CLOSE, () => {
        if (typeof dismissCallback === 'function') {
            dismissCallback();
        }
    });
    return element;
}
const ForgeBottomSheet = createOverlayComponent(controller$3);

function useDynamicForgeComponent(tagName, component, componentProps, delegate, parentElement) {
    const [isVisible, setIsVisible] = useState(false);
    const customElementRef = useRef();
    const parent = parentElement || document.body;
    useEffect(() => {
        if (isVisible && component && customElementRef.current) {
            if (React.isValidElement(component)) {
                ReactDOM.render(component, customElementRef.current);
            }
            else {
                ReactDOM.render(React.createElement(component, componentProps), customElementRef.current);
            }
        }
    }, [component, customElementRef.current, isVisible, componentProps]);
    function show(options) {
        if (customElementRef.current) {
            return customElementRef.current;
        }
        customElementRef.current = document.createElement(tagName);
        ReactDOM.createPortal(customElementRef.current, parent);
        // Apply any configuration options to our Forge component instance
        if (typeof options === 'object') {
            Object.assign(customElementRef.current, options);
        }
        setIsVisible(true);
        if (delegate && delegate.show) {
            delegate.show(customElementRef.current, options);
        }
        else {
            parent.appendChild(customElementRef.current);
        }
        return customElementRef.current;
    }
    function hide() {
        return __awaiter(this, void 0, void 0, function* () {
            if (!customElementRef.current || !customElementRef.current.isConnected) {
                return;
            }
            if (customElementRef.current) {
                if (delegate && delegate.hide) {
                    yield delegate.hide(customElementRef.current, () => {
                        if (customElementRef.current) {
                            ReactDOM.unmountComponentAtNode(customElementRef.current);
                        }
                    });
                }
                else {
                    ReactDOM.unmountComponentAtNode(customElementRef.current);
                    parent.removeChild(customElementRef.current);
                }
                customElementRef.current = undefined;
            }
            setIsVisible(false);
        });
    }
    return { show, hide };
}

const useForgeBusyIndicator = () => {
    const delegate = {
        hide: instance => instance.hide(true)
    };
    const controller = useDynamicForgeComponent('forge-busy-indicator', undefined, undefined, delegate);
    function show(_a = {}) {
        var { busyIndicatorClass, hostElement, busyIndicatorAttributes, cancelCallback } = _a, options = __rest(_a, ["busyIndicatorClass", "hostElement", "busyIndicatorAttributes", "cancelCallback"]);
        const busyIndicatorElement = controller.show(options);
        if (busyIndicatorAttributes) {
            busyIndicatorAttributes.forEach((value, key) => busyIndicatorElement.setAttribute(key, value));
        }
        if (busyIndicatorClass) {
            busyIndicatorElement.classList.add(busyIndicatorClass);
        }
        if (typeof cancelCallback === 'function') {
            busyIndicatorElement.addEventListener(BUSY_INDICATOR_CONSTANTS.events.CANCEL, () => cancelCallback());
        }
        return busyIndicatorElement;
    }
    return [show, controller.hide];
};

const ForgeBusyIndicator = ({ open, options }) => {
    const [showBusyIndicator, hideBusyIndicator] = useForgeBusyIndicator();
    useEffect(() => {
        if (open) {
            showBusyIndicator(options);
        }
        else {
            hideBusyIndicator();
        }
    }, [open]);
    return null;
};

const controller$2 = {
    create: createDialog,
    show: element => element.open = true,
    hide: element => {
        element.open = false;
    }
};
function createDialog({ options }, dismissCallback) {
    const element = document.createElement('forge-dialog');
    if (options) {
        const { dialogAttributes, dialogClass } = options, rest = __rest(options, ["dialogAttributes", "dialogClass"]);
        Object.assign(element, rest);
        if (dialogAttributes) {
            dialogAttributes.forEach((value, key) => element.setAttribute(key, value));
        }
        if (dialogClass) {
            element.classList.add(dialogClass);
        }
    }
    // Listen for the close event in case the bottom sheet is closed via escape key or backdrop click
    element.addEventListener(DIALOG_CONSTANTS.events.CLOSE, () => {
        if (typeof dismissCallback === 'function') {
            dismissCallback();
        }
    });
    return element;
}
const ForgeDialog = createOverlayComponent(controller$2);

const controller$1 = {
    create: createPopup,
    show: element => element.open = true,
    hide: element => {
        element.open = false;
    }
};
function createPopup({ options, targetElementRef }, dismissCallback) {
    const element = document.createElement('forge-popup');
    if (targetElementRef.current) {
        element.targetElement = targetElementRef.current;
        if (!options || options.static !== true) {
            targetElementRef.current.addEventListener(POPUP_CONSTANTS.events.BLUR, () => {
                if (typeof dismissCallback === 'function') {
                    dismissCallback();
                }
            });
        }
    }
    if (options) {
        const { popupAttributes, popupClass } = options, rest = __rest(options, ["popupAttributes", "popupClass"]);
        Object.assign(element, rest);
        if (popupAttributes) {
            popupAttributes.forEach((value, key) => element.setAttribute(key, value));
        }
        if (popupClass) {
            element.classList.add(popupClass);
        }
    }
    return element;
}
const ForgePopup = createOverlayComponent(controller$1);

const TOAST_CLOSE_DURATION = 250;
const controller = {
    create: createToast,
    show: el => document.body.appendChild(el),
    hide: el => new Promise(resolve => setTimeout(() => {
        el.hide();
        resolve();
    }, TOAST_CLOSE_DURATION)),
    update: (el, { options }) => {
        if (!options) {
            return;
        }
        const rest = __rest(options, ["actionHandler"]);
        if (rest) {
            Object.assign(el, rest);
        }
    }
};
function createToast({ options }, dismissCallback) {
    const element = document.createElement('forge-toast');
    if (options) {
        const { actionHandler } = options, rest = __rest(options, ["actionHandler"]);
        if (rest) {
            Object.assign(element, rest);
        }
        // Listen for the actions if the consumer cares to respond to it
        if (typeof actionHandler === 'function') {
            element.addEventListener(TOAST_CONSTANTS.events.ACTION, () => actionHandler());
        }
    }
    // Listen for the component automatically hiding itself
    element.addEventListener(TOAST_CONSTANTS.events.CLOSE, dismissCallback);
    return element;
}
const ForgeToast = createOverlayComponent(controller);

const BOTTOM_SHEET_CLOSE_ANIMATION_DURATION = 200;
const useForgeBottomSheet = (component, componentProps) => {
    const delegate = {
        show: instance => instance.open = true,
        // eslint-disable-next-line arrow-body-style
        hide: (instance, cb) => {
            return new Promise(resolve => {
                instance.open = false;
                setTimeout(() => {
                    if (typeof cb === 'function') {
                        cb();
                    }
                    resolve();
                }, BOTTOM_SHEET_CLOSE_ANIMATION_DURATION); // Wait for the close animation to complete
            });
        }
    };
    const controller = useDynamicForgeComponent('forge-bottom-sheet', component, componentProps, delegate);
    function show(_a = {}, dismissCallback) {
        var { bottomSheetAttributes, bottomSheetClass } = _a, options = __rest(_a, ["bottomSheetAttributes", "bottomSheetClass"]);
        const bottomSheetElement = controller.show(options);
        if (bottomSheetAttributes) {
            bottomSheetAttributes.forEach((value, key) => bottomSheetElement.setAttribute(key, value));
        }
        if (bottomSheetClass) {
            bottomSheetElement.classList.add(bottomSheetClass);
        }
        bottomSheetElement.addEventListener(BOTTOM_SHEET_CONSTANTS.events.CLOSE, () => {
            controller.hide();
            if (typeof dismissCallback === 'function') {
                dismissCallback();
            }
        });
        return bottomSheetElement;
    }
    return [show, controller.hide];
};

const useForgeDialog = (component, componentProps) => {
    const delegate = {
        show: instance => instance.open = true,
        hide: instance => {
            instance.open = false;
        }
    };
    const controller = useDynamicForgeComponent('forge-dialog', component, componentProps, delegate);
    function show(_a = {}, dismissCallback) {
        var { dialogAttributes, dialogClass } = _a, options = __rest(_a, ["dialogAttributes", "dialogClass"]);
        const dialogElement = controller.show(options);
        if (dialogAttributes) {
            dialogAttributes.forEach((value, key) => dialogElement.setAttribute(key, value));
        }
        if (dialogClass) {
            dialogElement.classList.add(dialogClass);
        }
        dialogElement.addEventListener(DIALOG_CONSTANTS.events.CLOSE, () => {
            controller.hide();
            if (typeof dismissCallback === 'function') {
                dismissCallback();
            }
        });
        return dialogElement;
    }
    return [show, controller.hide];
};

function useForgePopup(component, componentProps) {
    const delegate = {
        show: (instance, options) => {
            // Set default options
            instance.static = (options === null || options === void 0 ? void 0 : options.static) || false;
            instance.manageFocus = false;
            instance.animationType = PopupAnimationType.Dropdown;
            // We need to set the target element before the element is initialized to avoid an exception from Forge
            if (options === null || options === void 0 ? void 0 : options.targetElement) {
                instance.targetElement = options.targetElement;
            }
            instance.open = true;
        },
        // eslint-disable-next-line arrow-body-style
        hide: (instance, cb) => {
            return new Promise(resolve => {
                instance.open = false;
                setTimeout(() => {
                    if (typeof cb === 'function') {
                        cb();
                    }
                    resolve();
                }, POPUP_CONSTANTS.numbers.ANIMATION_DURATION); // Wait for the close animation to complete
            });
        }
    };
    const controller = useDynamicForgeComponent('forge-popup', component, componentProps, delegate);
    function show(targetElement, _a = {}, dismissCallback) {
        var { popupAttributes, popupClass } = _a, options = __rest(_a, ["popupAttributes", "popupClass"]);
        const popupElement = controller.show(Object.assign({ targetElement: targetElement.current }, options));
        if (popupAttributes) {
            popupAttributes.forEach((value, key) => popupElement.setAttribute(key, value));
        }
        if (popupClass) {
            popupElement.classList.add(popupClass);
        }
        if (options.static !== true) {
            targetElement.current.addEventListener(POPUP_CONSTANTS.events.BLUR, () => {
                controller.hide();
                if (typeof dismissCallback === 'function') {
                    dismissCallback();
                }
            });
        }
        return popupElement;
    }
    return [show, controller.hide];
}

const useForgeToast = (component, componentProps) => {
    const delegate = {
        // eslint-disable-next-line arrow-body-style
        hide: () => {
            return new Promise(resolve => {
                setTimeout(() => resolve(), 250); // Wait for the toast animation to complete
            });
        }
    };
    const controller = useDynamicForgeComponent('forge-toast', component, componentProps, delegate);
    function show(options, dismissCallback) {
        const toastElement = controller.show(options);
        // We need to attach a listener for the actions if the consumer cares to respond to it
        const actionHandler = options === null || options === void 0 ? void 0 : options.actionHandler;
        if (typeof actionHandler === 'function') {
            toastElement.addEventListener(TOAST_CONSTANTS.events.ACTION, () => actionHandler());
        }
        // We need to listen for the component automatically hiding itself so we can update our state
        toastElement.addEventListener(TOAST_CONSTANTS.events.CLOSE, () => {
            controller.hide();
            if (typeof dismissCallback === 'function') {
                dismissCallback();
            }
        });
        return toastElement;
    }
    return [show, controller.hide];
};

export { ForgeAccordion, ForgeAppBar, ForgeAppBarHelpButton, ForgeAppBarMenuButton, ForgeAppBarNotificationButton, ForgeAppBarProfileButton, ForgeAppBarSearch, ForgeAutocomplete, ForgeAvatar, ForgeBackdrop, ForgeBadge, ForgeBanner, ForgeBottomSheet, ForgeBusyIndicator, ForgeButton, ForgeButtonArea, ForgeButtonToggle, ForgeButtonToggleGroup, ForgeCalendar, ForgeCard, ForgeCheckbox, ForgeChip, ForgeChipField, ForgeChipSet, ForgeCircularProgress, ForgeColorPicker, ForgeDatePicker, ForgeDateRangePicker, ForgeDialog, ForgeDivider, ForgeDrawer, ForgeExpansionPanel, ForgeFilePicker, ForgeFloatingActionButton, ForgeIcon, ForgeIconButton, ForgeInlineMessage, ForgeKeyboardShortcut, ForgeLabelValue, ForgeLinearProgress, ForgeList, ForgeListItem, ForgeMenu, ForgeMiniDrawer, ForgeModalDrawer, ForgeOpenIcon, ForgeOption, ForgeOptionGroup, ForgePageState, ForgePaginator, ForgePopup, ForgeProductIcon, ForgeProfileCard, ForgeQuantityField, ForgeRadio, ForgeRipple, ForgeScaffold, ForgeSelect, ForgeSelectDropdown, ForgeSkeleton, ForgeSlider, ForgeSplitView, ForgeSplitViewPanel, ForgeStack, ForgeStep, ForgeStepper, ForgeSwitch, ForgeTab, ForgeTabBar, ForgeTable, ForgeTextField, ForgeTimePicker, ForgeToast, ForgeToolbar, ForgeTooltip, ForgeView, ForgeViewSwitcher, createElementProxy, useDynamicForgeComponent, useForgeBottomSheet, useForgeBusyIndicator, useForgeDialog, useForgePopup, useForgeToast };
//# sourceMappingURL=forge-react.js.map
